/**
 * Educational Content Grid Component
 * Implements IBM Carbon Design System layout for educational content display
 * Supports responsive design, SEO optimization, and multi-language content
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  ClickableTile,
  Tag,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Pagination,
  SkeletonText,
  InlineNotification,
  Search,
  Dropdown,
  Layer,
  Stack,
} from '@carbon/react';
import {
  View,
  Edit,
  TrashCan,
  ChevronRight,
  Calendar,
  User,
  Language,
  Analytics,
  UserMultiple,
  Education,
  UserAdmin,
  DataVis_1,
} from '@carbon/icons-react';
import { ArticleCard } from './ArticleCard';
import styles from './ContentGrid.module.scss';

interface EducationalContent {
  id: string;
  type: 'testimonial' | 'success-story' | 'demo-experience' | 'learning-outcome' | 'article' | 'guide';
  language: 'en' | 'pt';
  variant?: string;
  title: string;
  description: string;
  content: any;
  metadata?: {
    author?: string;
    institution?: string;
    tags?: string[];
    publishedAt?: string;
    updatedAt?: string;
    readingTime?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
    targetAudience?: 'parents' | 'educators' | 'administrators' | 'all';
  };
  seoData?: {
    metaTitle?: string;
    metaDescription?: string;
    keywords?: string[];
    canonicalUrl?: string;
    ogImage?: string;
    structuredData?: any;
  };
  performance?: {
    views: number;
    shares: number;
    engagementRate: number;
    conversionRate: number;
    seoRanking?: number;
  };
}

interface ContentGridProps {
  contentType?: string;
  language?: 'en' | 'pt';
  viewMode?: 'grid' | 'table' | 'list';
  enableSEOOptimization?: boolean;
  enableAnalytics?: boolean;
  onContentSelect?: (content: EducationalContent) => void;
  onContentEdit?: (content: EducationalContent) => void;
  onContentDelete?: (contentId: string) => void;
}

const CONTENT_TYPES = [
  { id: '', text: 'All Content Types' },
  { id: 'article', text: 'Articles' },
  { id: 'guide', text: 'Guides' },
  { id: 'testimonial', text: 'Testimonials' },
  { id: 'success-story', text: 'Success Stories' },
  { id: 'demo-experience', text: 'Demo Experiences' },
  { id: 'learning-outcome', text: 'Learning Outcomes' },
];

const AUDIENCE_FILTERS = [
  { id: '', text: 'All Audiences' },
  { id: 'parents', text: 'Parents' },
  { id: 'educators', text: 'Educators' },
  { id: 'administrators', text: 'Administrators' },
];

const DIFFICULTY_FILTERS = [
  { id: '', text: 'All Levels' },
  { id: 'beginner', text: 'Beginner' },
  { id: 'intermediate', text: 'Intermediate' },
  { id: 'advanced', text: 'Advanced' },
];

export const ContentGrid: React.FC<ContentGridProps> = ({
  contentType = '',
  language = 'en',
  viewMode = 'grid',
  enableSEOOptimization = true,
  enableAnalytics = true,
  onContentSelect,
  onContentEdit,
  onContentDelete,
}) => {
  const [content, setContent] = useState<EducationalContent[]>([]);
  const [filteredContent, setFilteredContent] = useState<EducationalContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContentType, setSelectedContentType] = useState(contentType);
  const [selectedAudience, setSelectedAudience] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [sortBy, setSortBy] = useState('publishedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Fetch educational content
  useEffect(() => {
    fetchContent();
  }, [selectedContentType, language]);

  // Filter and search content
  useEffect(() => {
    let filtered = [...content];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        item =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.metadata?.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Audience filter
    if (selectedAudience) {
      filtered = filtered.filter(
        item => item.metadata?.targetAudience === selectedAudience || item.metadata?.targetAudience === 'all'
      );
    }

    // Difficulty filter
    if (selectedDifficulty) {
      filtered = filtered.filter(item => item.metadata?.difficulty === selectedDifficulty);
    }

    // Sort content
    filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'title':
          aValue = a.title;
          bValue = b.title;
          break;
        case 'views':
          aValue = a.performance?.views || 0;
          bValue = b.performance?.views || 0;
          break;
        case 'engagementRate':
          aValue = a.performance?.engagementRate || 0;
          bValue = b.performance?.engagementRate || 0;
          break;
        case 'publishedAt':
        default:
          aValue = a.metadata?.publishedAt || '';
          bValue = b.metadata?.publishedAt || '';
          break;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });

    setFilteredContent(filtered);
    setCurrentPage(1);
  }, [content, searchQuery, selectedAudience, selectedDifficulty, sortBy, sortDirection]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = selectedContentType
        ? `/api/v1/educational-content/content/${selectedContentType}?lang=${language}`
        : `/api/v1/educational-content/content/article?lang=${language}`;

      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error('Failed to fetch content');
      }

      const result = await response.json();
      if (result.success) {
        setContent(result.data || []);
      } else {
        throw new Error(result.error || 'Failed to load content');
      }
    } catch (err) {
      console.error('Content fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentAction = async (action: string, contentItem: EducationalContent) => {
    switch (action) {
      case 'view':
        onContentSelect?.(contentItem);
        break;
      case 'edit':
        onContentEdit?.(contentItem);
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to delete this content?')) {
          onContentDelete?.(contentItem.id);
          // Refresh content
          await fetchContent();
        }
        break;
      case 'analyze':
        // Track analytics event for content analysis
        if (enableAnalytics) {
          trackContentAnalytics(contentItem.id, 'analyze');
        }
        break;
    }
  };

  const trackContentAnalytics = async (contentId: string, action: string) => {
    try {
      await fetch('/api/v1/analytics/events/privacy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: 'content_interaction',
          contentId,
          action,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  };

  const getTagType = (difficulty?: string) => {
    // Using Carbon semantic color types for tags
    switch (difficulty) {
      case 'beginner':
        return 'green';
      case 'intermediate':
        return 'blue';
      case 'advanced':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getAudienceIcon = (audience?: string) => {
    switch (audience) {
      case 'parents':
        return UserMultiple;
      case 'educators':
        return Education;
      case 'administrators':
        return UserAdmin;
      default:
        return UserMultiple;
    }
  };

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContent = filteredContent.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredContent.length / itemsPerPage);

  // Table headers for table view
  const tableHeaders = [
    { key: 'title', header: 'Title' },
    { key: 'type', header: 'Type' },
    { key: 'audience', header: 'Audience' },
    { key: 'language', header: 'Language' },
    { key: 'publishedAt', header: 'Published' },
    { key: 'performance', header: 'Performance' },
    { key: 'actions', header: 'Actions' },
  ];

  if (loading) {
    return (
      <div className={styles.contentGrid}>
        <div className={styles.header}>
          <SkeletonText heading width="40%" />
          <SkeletonText width="60%" />
        </div>
        <Grid>
          {[...Array(6)].map((_, index) => (
            <Column key={index} sm={4} md={4} lg={4}>
              <Tile className={styles.skeletonTile}>
                <SkeletonText heading width="80%" />
                <SkeletonText width="100%" />
                <SkeletonText width="60%" />
              </Tile>
            </Column>
          ))}
        </Grid>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.contentGrid}>
        <InlineNotification
          kind="error"
          title="Error loading content"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
        />
        <Button kind="primary" onClick={fetchContent} className={styles.loadMoreButton}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.contentGrid}>
      {/* Header and Filters */}
      <div className={styles.header}>
        <Stack gap={5}>
          <h2 className="cds--type-heading-04">Educational Content Library</h2>
          <p className="cds--type-body-long-01">
            Manage and optimize educational content with SEO analytics and Carbon Design System components.
          </p>

          {/* Search and Filters */}
          <Grid>
            <Column sm={4} md={4} lg={6}>
              <Search
                size="lg"
                placeholder="Search content..."
                labelText="Search content"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </Column>
            <Column sm={4} md={2} lg={2}>
              <Dropdown
                id="content-type-filter"
                titleText="Content Type"
                label="Select type"
                items={CONTENT_TYPES}
                selectedItem={CONTENT_TYPES.find(t => t.id === selectedContentType)}
                onChange={({ selectedItem }) => setSelectedContentType(selectedItem?.id || '')}
              />
            </Column>
            <Column sm={4} md={2} lg={2}>
              <Dropdown
                id="audience-filter"
                titleText="Target Audience"
                label="Select audience"
                items={AUDIENCE_FILTERS}
                selectedItem={AUDIENCE_FILTERS.find(a => a.id === selectedAudience)}
                onChange={({ selectedItem }) => setSelectedAudience(selectedItem?.id || '')}
              />
            </Column>
            <Column sm={4} md={2} lg={2}>
              <Dropdown
                id="difficulty-filter"
                titleText="Difficulty Level"
                label="Select level"
                items={DIFFICULTY_FILTERS}
                selectedItem={DIFFICULTY_FILTERS.find(d => d.id === selectedDifficulty)}
                onChange={({ selectedItem }) => setSelectedDifficulty(selectedItem?.id || '')}
              />
            </Column>
          </Grid>
        </Stack>
      </div>

      {/* Content Display */}
      {viewMode === 'grid' && (
        <Grid>
          {paginatedContent.map(contentItem => (
            <Column key={contentItem.id} sm={4} md={4} lg={4}>
              <ArticleCard
                content={contentItem}
                onAction={action => handleContentAction(action, contentItem)}
                enableSEOOptimization={enableSEOOptimization}
                enableAnalytics={enableAnalytics}
              />
            </Column>
          ))}
        </Grid>
      )}

      {viewMode === 'table' && (
        <DataTable rows={paginatedContent} headers={tableHeaders}>
          {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => {
                    const contentItem = paginatedContent.find(c => c.id === row.id);
                    return (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        <TableCell>
                          <Stack gap={2}>
                            <span className="cds--type-label-01">{contentItem?.title}</span>
                            <div>
                              {contentItem?.metadata?.tags?.slice(0, 3).map(tag => (
                                <Tag key={tag} type="outline" size="sm">
                                  {tag}
                                </Tag>
                              ))}
                            </div>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tag type="outline" size="sm">
                            {contentItem?.type}
                          </Tag>
                        </TableCell>
                        <TableCell>
                          <Stack orientation="horizontal" gap={2}>
                            {React.createElement(getAudienceIcon(contentItem?.metadata?.targetAudience), { size: 16 })}
                            <span>{contentItem?.metadata?.targetAudience || 'All'}</span>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tag type={contentItem?.language === 'en' ? 'blue' : 'green'} size="sm">
                            {contentItem?.language?.toUpperCase()}
                          </Tag>
                        </TableCell>
                        <TableCell>
                          {contentItem?.metadata?.publishedAt
                            ? new Date(contentItem.metadata.publishedAt).toLocaleDateString()
                            : 'Draft'}
                        </TableCell>
                        <TableCell>
                          <Stack gap={2}>
                            <Stack orientation="horizontal" gap={2}>
                              <View size={16} />
                              <span className="cds--type-helper-text-01">
                                {contentItem?.performance?.views || 0} views
                              </span>
                            </Stack>
                            <Stack orientation="horizontal" gap={2}>
                              <DataVis_1 size={16} />
                              <span className="cds--type-helper-text-01">
                                {contentItem?.performance?.engagementRate || 0}% engagement
                              </span>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack orientation="horizontal" gap={2}>
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={View}
                              iconDescription="View content"
                              onClick={() => handleContentAction('view', contentItem!)}
                            />
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={Edit}
                              iconDescription="Edit content"
                              onClick={() => handleContentAction('edit', contentItem!)}
                            />
                            <Button
                              kind="ghost"
                              size="sm"
                              renderIcon={Analytics}
                              iconDescription="Analyze performance"
                              onClick={() => handleContentAction('analyze', contentItem!)}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}

      {/* Empty State */}
      {filteredContent.length === 0 && !loading && (
        <Layer>
          <Tile className={styles.emptyState}>
            <Stack gap={5}>
              <h3 className="cds--type-heading-03">No content found</h3>
              <p className="cds--type-body-long-01">
                Try adjusting your search criteria or create new educational content.
              </p>
              <Button kind="primary">Create New Content</Button>
            </Stack>
          </Tile>
        </Layer>
      )}

      {/* Pagination */}
      {filteredContent.length > itemsPerPage && (
        <Pagination
          page={currentPage}
          totalItems={filteredContent.length}
          pageSize={itemsPerPage}
          pageSizes={[12, 24, 36]}
          onChange={({ page, pageSize }) => {
            setCurrentPage(page);
            setItemsPerPage(pageSize);
          }}
        />
      )}
    </div>
  );
};

export default ContentGrid;
